import { UISlider } from '../../components/slider/ui-slider';
import type { ComponentPlugin } from '../../components/ui-element.type';
import { Digest } from '../../global/digest';
import { adoptStyle, insertElements } from '../../global/ui-helpers';
import type { NullOr } from '../../global/utils.type';
import styles from './ui-slider-limit-offer.css';

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for ui-slider offer plugin.
 * @alias UISliderLimitOffer
 * @property {number} [limitOffer] {@attr limit-offer} Adds offer limit to slider.
 * @property {string} [labelLimitOffer] {@attr label-limit-offer} Adds offer
 * limit label for slider.
 * @param {UIElement} target
 * @implements {ComponentPlugin}
 */
export class UISliderLimitOffer implements ComponentPlugin {
    private readonly target: UISlider;
    private limitOfferPositioner!: () => void;
    private _handleDescriptionId?: string;

    constructor(target: UISlider) {
        this.target = target;
    }

    get limitOffer(): number {
        return Number(this.target.getAttribute('limit-offer'));
    }

    set limitOffer(value: number) {
        this.target.setAttribute('limit-offer', String(!value ? 0 : value));
        this.updateLimitLabel();
        this.setLimitOfferPosition();
    }

    get labelLimitOffer(): NullOr<string> {
        return this.target.getAttribute('label-limit-offer');
    }

    set labelLimitOffer(value: string) {
        this.target.setAttribute('label-limit-offer', value);
        this.updateLimitLabel();
        this.setLimitOfferPosition();
    }

    get offer(): NullOr<HTMLDivElement> {
        return this.target.querySelector<HTMLDivElement>(
            '.ui-slider__limit-offer'
        );
    }

    get handle(): NullOr<HTMLSpanElement> {
        return this.target.querySelector<HTMLSpanElement>('.ui-slider__handle');
    }

    get offerLabel(): NullOr<HTMLSpanElement> {
        return this.target.querySelector<HTMLSpanElement>(
            '.ui-slider__label-limit-offer'
        );
    }

    get offerLabelArrow(): NullOr<HTMLSpanElement> {
        return this.target.querySelector<HTMLSpanElement>(
            '.ui-slider__label-limit-offer-arrow'
        );
    }

    private get handleDescriptionUid(): string {
        return (this._handleDescriptionId =
            this._handleDescriptionId || Digest.randomId());
    }

    /**
     * Formats label offer limit, replaced sub-sting {limit} with value.
     * @returns {string}
     */
    formatLabelLimitOffer(): string {
        return String(this.labelLimitOffer).replace(
            '{limit}',
            String(this.limitOffer)
        );
    }

    /**
     * Updates limit label.
     */
    updateLimitLabel(): void {
        if (!this.offerLabel) {
            return;
        }
        this.offerLabel.innerText = this.formatLabelLimitOffer();
    }

    /**
     * Sets limit offer position.
     */
    setLimitOfferPosition(): void {
        const lim =
            this.limitOffer > this.target.max
                ? this.target.max
                : this.limitOffer;
        const progress = this.target.calcProgress(lim);
        if (lim && this.offer) {
            this.target.adjustElementPosition(this.offer, progress);
        }
        if (this.labelLimitOffer) {
            if (this.offerLabel) {
                this.target.adjustElementPosition(this.offerLabel, progress);
            }
            if (this.offerLabelArrow) {
                this.target.adjustElementPosition(
                    this.offerLabelArrow,
                    progress
                );
            }
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        if (this.limitOffer) {
            insertElements(this.target.bar, [
                {
                    tagName: 'div',
                    classList: {
                        'ui-slider__limit-offer': true,
                    },
                },
            ]);
        }

        if (this.labelLimitOffer) {
            insertElements(this.target.container, [
                {
                    tagName: 'div',
                    classList: {
                        'ui-slider__additional-limit': true,
                    },
                    children: [
                        {
                            tagName: 'span',
                            classList: {
                                'ui-slider__label-limit-offer-arrow': true,
                            },
                        },
                        {
                            tagName: 'span',
                            classList: {
                                'ui-slider__label-limit-offer': true,
                            },
                            attributes: { id: this.handleDescriptionUid },
                            children: [this.formatLabelLimitOffer()],
                        },
                    ],
                },
            ]);

            this.handle?.setAttribute(
                'aria-describedby',
                this.handleDescriptionUid
            );
        }
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.limitOfferPositioner = this.setLimitOfferPosition.bind(this);
        this.target.addEventListener('sync', this.limitOfferPositioner);
        window.addEventListener('resize', this.limitOfferPositioner);
        this.limitOfferPositioner();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        window.removeEventListener('resize', this.limitOfferPositioner);
        this.target.removeEventListener('sync', this.limitOfferPositioner);
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        window.addEventListener('resize', this.limitOfferPositioner);
        this.target.addEventListener('sync', this.limitOfferPositioner);
    }
}

UISlider.registerPlugin('limit-offer', UISliderLimitOffer);
adoptStyle(styles, 'ui-slider-limit-offer-style').catch((err: unknown) =>
    console.error(err)
);
